import { useState } from "react";
import { Save, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const ROLE_TOGGLE_KEYS = ["medicaid", "background_check", "drug_screen", "tb_test", "child_abuse_clearance", "cpr_first_aid"];

const RolePicker = ({ roles, selected, blocked = [], onChange, testId }) => (
  <div className="compliance-role-picker" data-testid={testId}>
    {roles.map((role) => {
      const disabled = blocked.includes(role.key);
      return (
        <label key={role.key} className={`compliance-role-chip ${selected.includes(role.key) ? "is-on" : ""} ${disabled ? "is-blocked" : ""}`} title={disabled ? "Never covered by Medicaid enrollment" : ""}>
          <input type="checkbox" checked={selected.includes(role.key)} disabled={disabled} data-testid={`${testId}-${role.key}`} onChange={(e) => onChange(e.target.checked ? [...selected, role.key] : selected.filter((r) => r !== role.key))} />
          {role.label}
        </label>
      );
    })}
  </div>
);

export const ClientRequirementsEditor = ({ client, catalog, canEdit, onSaved }) => {
  const [draft, setDraft] = useState(client.requirements);
  const [custom, setCustom] = useState({ name: "", roles: catalog.roles.map((r) => r.key) });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const setEntry = (key, patch) => setDraft((d) => ({ ...d, [key]: { ...d[key], ...patch } }));

  async function save() {
    setBusy(true);
    setError("");
    try {
      const response = await api.put(`/compliance/requirements/${client.client_id}`, draft);
      setDraft(response.data.requirements);
      setNotice(response.data.changes.length ? `${response.data.changes.length} change(s) saved and logged.` : "No changes.");
      onSaved(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="compliance-editor" data-testid={`requirements-editor-${client.client_id}`}>
      {ROLE_TOGGLE_KEYS.map((key) => {
        const entry = draft[key];
        const label = catalog.requirements.find((r) => r.key === key)?.label || key;
        return (
          <section key={key} className={`compliance-requirement ${entry.enabled ? "is-on" : ""}`} data-testid={`requirement-${key}-${client.client_id}`}>
            <header>
              <label className="compliance-switch">
                <input type="checkbox" role="switch" checked={entry.enabled} disabled={!canEdit} data-testid={`toggle-${key}-${client.client_id}`} onChange={(e) => setEntry(key, { enabled: e.target.checked })} />
                <span>{label}</span>
              </label>
              {key === "medicaid" && (
                <select value={entry.jurisdiction} disabled={!canEdit} data-testid={`medicaid-jurisdiction-${client.client_id}`} onChange={(e) => setEntry(key, { jurisdiction: e.target.value })}>
                  {catalog.jurisdictions.map((j) => <option key={j}>{j}</option>)}
                </select>
              )}
            </header>
            {entry.enabled && (
              <RolePicker roles={catalog.roles} selected={entry.roles} blocked={key === "medicaid" ? catalog.never_medicaid_roles : []} testId={`roles-${key}-${client.client_id}`} onChange={canEdit ? (roles) => setEntry(key, { roles }) : () => {}} />
            )}
          </section>
        );
      })}
      <section className={`compliance-requirement ${draft.show_provider_clearance ? "is-on" : ""}`} data-testid={`requirement-show-clearance-${client.client_id}`}>
        <header>
          <label className="compliance-switch">
            <input type="checkbox" role="switch" checked={Boolean(draft.show_provider_clearance)} disabled={!canEdit} data-testid={`toggle-show-clearance-${client.client_id}`} onChange={(e) => setDraft((d) => ({ ...d, show_provider_clearance: e.target.checked }))} />
            <span>Show provider clearance to this school</span>
          </label>
        </header>
        <p className="compliance-help">Off by default. When on, the school's portal lists only placed providers whose required items are all on file — license status, missing or expired items are never shown to a client.</p>
      </section>
      <section className="compliance-requirement is-custom" data-testid={`requirement-custom-${client.client_id}`}>
        <header><span className="compliance-custom-title">Custom requirements</span></header>
        {draft.custom.map((item) => (
          <div key={item.id} className="compliance-custom-row" data-testid={`custom-requirement-${item.id}`}>
            <label className="compliance-switch">
              <input type="checkbox" role="switch" checked={item.enabled} disabled={!canEdit} onChange={(e) => setDraft((d) => ({ ...d, custom: d.custom.map((c) => (c.id === item.id ? { ...c, enabled: e.target.checked } : c)) }))} />
              <span>{item.name}</span>
            </label>
            {canEdit && <button type="button" className="table-edit-button table-delete-button" data-testid={`remove-custom-${item.id}`} onClick={() => setDraft((d) => ({ ...d, custom: d.custom.filter((c) => c.id !== item.id) }))}>Remove</button>}
            <RolePicker roles={catalog.roles} selected={item.roles} testId={`roles-${item.id}`} onChange={canEdit ? (roles) => setDraft((d) => ({ ...d, custom: d.custom.map((c) => (c.id === item.id ? { ...c, roles } : c)) })) : () => {}} />
          </div>
        ))}
        {canEdit && (
          <div className="compliance-custom-add">
            <input placeholder="Requirement name (e.g. Fingerprinting)" value={custom.name} data-testid={`custom-name-${client.client_id}`} onChange={(e) => setCustom({ ...custom, name: e.target.value })} />
            <button type="button" className="button button-ghost" data-testid={`add-custom-${client.client_id}`} disabled={!custom.name.trim()} onClick={() => { setDraft((d) => ({ ...d, custom: [...d.custom, { id: `custom-${Date.now()}`, name: custom.name.trim(), enabled: true, roles: custom.roles }] })); setCustom({ ...custom, name: "" }); }}>Add custom requirement</button>
          </div>
        )}
      </section>
      {error && <p className="form-message error-message" data-testid={`requirements-error-${client.client_id}`}>{error}</p>}
      {notice && <p className="form-message success-message" data-testid={`requirements-notice-${client.client_id}`}>{notice}</p>}
      {canEdit ? (
        <button type="button" className="button button-primary" data-testid={`save-requirements-${client.client_id}`} disabled={busy} onClick={save}><Save size={15} /> {busy ? "Saving…" : "Save requirements"}</button>
      ) : (
        <p className="pairing-meta"><ShieldCheck size={13} /> Only the System Administrator and HR can change these toggles.</p>
      )}
    </div>
  );
};
